<template>
  <nav class="dui-pagination pagination">
    <a
      v-if="showNextAndPrev"
      class="previous"
      :disabled="vm.currentPage === 1"
      :href="getUrlForPageNumber(vm.currentPage - 1)"
      title="Previous page"
      @click="goToPreviousPage()"
    >
      <span class="hidden-until-sm">Previous page</span>
    </a>

    <span
      v-if="showPageNumbers"
      class="pages"
    >
      <span v-if="vm.showStartPage">
        <a
          class="first-page"
          :href="getUrlForPageNumber(1)"
          title="First page"
          @click="goToPage(1)"
        >1</a>
        <span>...</span>
      </span>

      <a
        v-for="n in vm.displayPages"
        :key="n"
        class="inner-page"
        :class="{ 'active': (n === vm.currentPage) }"
        :href="getUrlForPageNumber(n)"
        :title="n"
        @click="goToPage(n)"
      >{{ n }}</a>

      <span v-if="vm.showEndPage">
        <span>...</span>
        <a
          class="last-page"
          :href="getUrlForPageNumber(totalPages)"
          title="Last page"
          @click="goToPage(totalPages)"
        >{{ totalPages }}</a>
      </span>
    </span>

    <a
      v-if="showNextAndPrev"
      class="next"
      :disabled="vm.currentPage === totalPages"
      :href="getUrlForPageNumber(vm.currentPage + 1)"
      title="Next page"
      @click="goToNextPage()"
    >
      <span class="hidden-until-sm">Next page</span>
    </a>
  </nav>
</template>

<script>
export default {
  props: {
    anchor: {
      type: String,
      default: null,
    },
    baseUrl: {
      type: String,
      default: null,
    },
    navigate: {
      type: Boolean,
      default: false,
    },
    numberOfPages: {
      type: Number,
      default: 5,
    },
    pageType: {
      type: String,
      default: null,
      validator: function (value) {
        return [
          'param',
          'url',
        ].indexOf(value) !== -1;
      },
    },
    showNextAndPrev: {
      type: Boolean,
      default: true,
    },
    showPageNumbers: {
      type: Boolean,
      default: true,
    },
    showStartAndEndPages: {
      type: Boolean,
      default: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    value: {
      type: Number,
      default: 1,
    },
  },
  data: () => ({
    vm: {
      currentPage: 1,
      displayPages: [],
      showEndPage: false,
      showStartPage: false,
      urlPattern: null,
    },
  }),
  mounted() {
    this.$set(this.vm, 'currentPage', this.value);
    this.calculateDisplayPages();
  },
  methods: {
    calculateDisplayPages() {
      if (!this.showPageNumbers) {
        return;
      }

      const numbers = [];
      const numOtherPagesDisplayed = Math.floor(this.numberOfPages / 2);

      let start = (this.vm.currentPage - numOtherPagesDisplayed > 0)
        ? (this.vm.currentPage - numOtherPagesDisplayed)
        : 1;

      let end = this.vm.currentPage + (this.numberOfPages - 1) - (this.vm.currentPage - start);
      // end cannot surpass the total pages
      end = (end > this.totalPages) ? this.totalPages : end;

      // Make sure we always display the right number of options when reaching the end
      start = (end - start) < (this.numberOfPages - 1) ? (end - (this.numberOfPages - 1)) : start;
      // start must be 1 or bigger
      start = (start > 0) ? start : 1;

      for (let i = start; i <= end; i++) {
        numbers.push(i);
      }

      if (this.showStartAndEndPages) {
        const showEndPage = !numbers.includes(this.totalPages);
        const showStartPage = !numbers.includes(1);
        this.$set(this.vm, 'showEndPage', showEndPage);
        this.$set(this.vm, 'showStartPage', showStartPage);
      }

      this.$set(this.vm, 'displayPages', numbers);
    },

    getUrlForPageNumber(pageNumber) {
      if (!this.navigate || !this.baseUrl) {
        return null;
      }

      if (!this.vm.urlPattern) {
        this.setupUrls();
      }

      if (pageNumber < 1 || pageNumber > this.totalPages) {
        return null;
      }

      return this.vm.urlPattern.replace('<page-number>', pageNumber);
    },

    goToNextPage() {
      if (this.navigate) {
        return;
      }

      if (this.vm.currentPage >= this.totalPages) {
        return;
      }

      this.goToPage(this.vm.currentPage + 1);
    },

    goToPage(pageNumber) {
      if (this.navigate) {
        return;
      }

      this.$set(this.vm, 'currentPage', pageNumber);
      this.calculateDisplayPages();
      this.$emit('input', this.vm.currentPage);
      this.$emit('change');
    },

    goToPreviousPage() {
      if (this.navigate) {
        return;
      }

      if (this.vm.currentPage < 2) {
        return;
      }

      this.goToPage(this.vm.currentPage - 1);
    },

    setupUrls() {
      if (!this.navigate) {
        return;
      }

      const anchor = this.anchor ? `#${ this.anchor }` : '';
      const urlPattern = [
        this.baseUrl,
        (this.pageType === 'param') ? '?page=<page-number>' : '/page/<page-number>',
        anchor,
      ].join('');

      this.$set(this.vm, 'urlPattern', urlPattern);
    },
  },
};
</script>
